/**
 * Iteration 294 — Commercial Participation Agreement (PTA & Charity
 * Partner Terms). Single source of truth for the full 6-section text,
 * rendered in org onboarding (KidsManage), the org Legal & Compliance
 * dashboard card, and the public /legal/partner-agreement page.
 */

export const AGREEMENT_VERSION = "1.0";

export const PARTNER_AGREEMENT_SECTIONS = [
  {
    n: 1,
    title: "Purpose & Overview",
    body: [
      "This Commercial Participation Agreement (the \"Agreement\") governs the relationship between Digitally Prepared Ltd, trading as MyTableTopApp (\"the Platform\"), and the named organization (\"the Partner\") — including PTAs, schools, registered charities, youth and sports clubs, community groups, and corporate sponsors.",
      "The Platform enables three participation routes: (a) Direct Listings — items listed by the Partner itself on its own verified shopfront; (b) Parent Listings — items listed by parent sellers who tag the Partner to receive an agreed share of proceeds; and (c) CSR Drawdowns — purchases funded through the Community Sponsorship Purchase Fund or corporate partners on the Partner's behalf.",
      "By registering an organization account, the Partner agrees to be bound by this Agreement across all three routes.",
    ],
  },
  {
    n: 2,
    title: "Commercial Participator Framework & UK Charity Law Compliance",
    body: [
      "For the purposes of Part II of the Charities Act 1992 and the Charities (Protection and Social Investment) Act 2016, the Platform acts as a commercial participator whenever it promotes sales on the basis that funds will be given to, or applied for the benefit of, a charitable institution.",
      "This Agreement constitutes the written agreement required by section 59 of the Charities Act 1992 between a commercial participator and a charitable institution.",
      "Every cause-tagged listing and checkout displays a statutory solicitation statement declaring how the purchase benefits the Partner, as required by the Charities Act 1992 and its associated regulations.",
      "The Partner confirms it is entitled to receive the funds, is responsible for applying them to its stated charitable or community purposes, and will notify the Platform immediately if its registration or charity status changes.",
    ],
  },
  {
    n: 3,
    title: "Listing Mechanics & Automated School/Cause Tagging",
    body: [
      "Parents and guardians may tag a verified Partner when creating a listing (\"Fundraise for a Cause\"), selecting the percentage of net proceeds — 100%, 75%, 50% or 25% — donated to the Partner.",
      "Tagged listings automatically display the Partner's name and campaign tag (e.g. \"Fundraising for: [Partner Name]\") on browse cards, listing pages, order receipts and ledger entries.",
      "The Partner authorizes the Platform to display its name, logo and custom campaign tags on parent-created listings that select the Partner as beneficiary.",
      "The Platform records each donation split automatically at checkout completion and attributes it to the Partner's fundraising ledger, visible on the Partner's cause dashboard.",
    ],
  },
  {
    n: 4,
    title: "Operational Rules & Hyper-Local Delivery",
    body: [
      "The marketplace is hyper-local by design: fulfilment is via Parent Handover, At/After School Collection, or Royal Mail Post where the seller enables it.",
      "Zero-Carbon Rule: the Partner should prioritise local meetup and school-gate collection so pre-loved goods circulate within the community without additional delivery mileage.",
      "All items must be accurately described, safe, and lawful for sale. The Platform may moderate, archive or remove any listing that breaches community or safety rules.",
      "Cause-tagged listings carry audited archive controls: once engaged (reserved, offers or messages), they cannot be withdrawn without an audited reason.",
    ],
  },
  {
    n: 5,
    title: "Financial Mechanics & Automated Payouts",
    body: [
      "Buyer payments are processed by Stripe. On a cause-tagged sale, seller proceeds are calculated as the item price minus the Platform's 10% technology and administration fee and the standard Stripe payment processing fee (currently 1.5% + 20p).",
      "The Partner's agreed percentage of net proceeds is recorded automatically at settlement and paid directly to the Partner's nominated bank account via Stripe Connect — no manual invoicing and no cash handling.",
      "The Platform fee is fixed at 10% across all account types. Stripe processing fees are set by Stripe and passed through at cost; the Platform takes no additional margin on them.",
      "Organization payouts are bank-transfer only, to the registered organizational bank account, following verification of the Partner's details.",
    ],
  },
  {
    n: 6,
    title: "Term, Termination & Digital Sign-off",
    body: [
      "This Agreement begins when the authorized representative completes digital sign-off at registration and continues until terminated.",
      "Either party may terminate at any time with written notice. Outstanding recorded donations are settled to the Partner's registered bank account within 30 days of termination.",
      "Digital sign-off — comprising the authorized representative's declaration checkboxes, signatory role, timestamp and network address — constitutes a legally binding signature.",
      "The Platform may update these terms with 30 days' notice; continued use after the notice period constitutes acceptance. A record of each signed version is retained for audit.",
    ],
  },
];

export const ATTESTATION_ITEMS = [
  "I am an authorized representative of the named PTA, Charity, or Community Group.",
  "I agree to the Commercial Participation Terms with Digitally Prepared Ltd (t/a MyTableTopApp).",
  "I authorize MyTableTopApp to display our organization's name, logo, and custom campaign tags (e.g., Fundraising for: [Name]) on parent-created listings.",
  "I agree to connect our bank account via Stripe Connect to receive direct, automated fundraising payouts.",
];

/**
 * Buyer-facing fundraising contribution for a cause-tagged listing: the
 * cause's split of seller proceeds (item price − 10% platform tech fee),
 * rounded UP in the cause's favour — parity with the iteration-288
 * settlement engine. Returns pounds.
 */
export function causeContributionGBP(priceGBP, splitPct = 100) {
  const itemPence = Math.round((priceGBP || 0) * 100);
  const platformPence = Math.round(itemPence * 0.10);
  const netPence = Math.max(0, itemPence - platformPence);
  return Math.ceil((netPence * (splitPct || 100)) / 100) / 100;
}

/** Statutory solicitation statement rendered at checkout for cause-tagged items. */
export function solicitationStatement(causeName) {
  return `100% of seller proceeds (minus 10% platform tech fee and standard Stripe processing) go directly to ${causeName || "the partner organization"}, facilitated by MyTableTopApp (operated by Digitally Prepared Ltd).`;
}
